import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Builder } from '@builder.io/react/lite';
import styled from 'styled-components';
import { addIdAttribute } from '@glowforge/builder-components/lib/util/shared-configs';

import useUser from '../../context/UserContext/useUser';
import { fetchState } from '../../context/UserContext';
import { resolveMyReferrerProfile } from '../../api/referrals';
import { redirectToLogin } from '../../util/authenticate';
import { buildReferralShareUrl } from '../../context/ReferralContext';

const VIEW_STATE = {
  ERROR: 'error',
  LOADING: 'loading',
  LOGGED_OUT: 'logged-out',
  READY: 'ready',
};

const previewReferrer = {
  referralCode: 'gf-preview1234',
  stats: {
    clicksCount: 12,
    referralsCount: 3,
    rewardsCount: 2,
  },
};

const DEFAULT_BACKGROUND_IMAGE =
  '//images.ctfassets.net/ljtyf78xujn2/4U7Fe73owMa6yuKm0UyqmI/'
  + '92bbd608e37f0d55791c8acee3944628/Bailey_S.jpg';

const DEFAULT_DESCRIPTION = [
  'Give your friends $500 off a Pro or Pro HD, $250 off a Plus or Plus HD,',
  'a $50 gift card with Glowforge Aura, or a $25 gift card with Glowforge Spark',
  'and earn equal Glowforge credit for yourself after their purchase.',
].join(' ');

const Widget = styled.section`
  background-color: #d6c2aa;
  background-image: ${({ $backgroundImage }) =>
    $backgroundImage ? `url(${$backgroundImage})` : 'none'};
  background-position: ${({ $backgroundPosition }) => $backgroundPosition};
  background-repeat: no-repeat;
  background-size: cover;
  border-radius: 8px;
  min-height: 48rem;
  overflow: hidden;
  padding: 1.25rem;
  position: relative;

  @media (min-width: 768px) {
    min-height: 50rem;
    padding: 3.5rem;
  }
`;

const Panel = styled.div`
  background: ${({ $panelBackgroundColor }) => $panelBackgroundColor};
  color: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 100%;
  padding: 1.75rem 1.5rem 3.5rem;
  position: relative;
  width: 100%;

  @media (min-width: 768px) {
    max-width: 36rem;
    min-height: 42rem;
    padding: 2.75rem 3.75rem 4rem;
  }
`;

const PanelBody = styled.div`
  display: grid;
  gap: 1.5rem;
`;

const Label = styled.p`
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  margin: 0;
  text-align: center;
`;

const HeadlineBand = styled.div`
  margin: 0 -1.5rem;
  padding: 1rem 1.5rem;

  @media (min-width: 768px) {
    margin: 0 -3.5rem;
    padding: 1.5rem 3.5rem;
  }
`;

const Headline = styled.h2`
  color: ${({ $accentColor }) => $accentColor};
  font-size: clamp(2.25rem, 4vw, 3.5rem);
  line-height: 1.05;
  margin: 0;
  text-align: center;
`;

const Description = styled.p`
  font-size: 1rem;
  font-weight: 300;
  line-height: 1.45;
  margin: 0;
  text-align: center;
  white-space: pre-line;

  @media (min-width: 768px) {
    font-size: 1.1rem;
  }
`;

const StateStack = styled.div`
  display: grid;
  gap: 1.25rem;
  justify-items: center;
`;

const StateMessage = styled.p`
  font-size: 1.05rem;
  line-height: 1.45;
  margin: 0;
  text-align: center;
  white-space: pre-line;
`;

const Callout = styled.p`
  font-size: 1.1rem;
  font-weight: 700;
  line-height: 1.4;
  margin: 0;
  text-align: center;
  white-space: pre-line;
`;

const PrimaryButton = styled.button`
  align-items: center;
  appearance: none;
  background: ${({ $accentColor }) => $accentColor};
  border: 0;
  border-radius: 999px;
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  font-size: 1.1rem;
  font-weight: 700;
  gap: 0.5rem;
  justify-content: center;
  min-height: 3rem;
  padding: 0.875rem 1.5rem;
  text-decoration: none;
  width: min(100%, 17rem);

  &:hover {
    filter: brightness(1.05);
  }
`;

const CopySection = styled.div`
  display: grid;
  gap: 0.75rem;
`;

const SectionLabel = styled.p`
  font-size: 0.875rem;
  margin: 0;
  text-align: center;
`;

const CopyRow = styled.div`
  display: grid;
  gap: 0.875rem;

  @media (min-width: 768px) {
    align-items: center;
    grid-template-columns: minmax(0, 1fr) auto;
  }
`;

const LinkValue = styled.div`
  background: #ffffff;
  border-radius: 999px;
  color: #1f2021;
  font-size: 1rem;
  min-height: 3rem;
  overflow-wrap: anywhere;
  padding: 0.875rem 1rem;
`;

const SecondaryButton = styled.button`
  align-items: center;
  appearance: none;
  background: ${({ $accentColor }) => $accentColor};
  border: 0;
  border-radius: 999px;
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  font-size: 1.1rem;
  font-weight: 700;
  justify-content: center;
  min-height: 3rem;
  padding: 0.875rem 1.75rem;
  width: 100%;

  @media (min-width: 768px) {
    width: auto;
  }

  &:hover {
    filter: brightness(1.05);
  }
`;

const StatsGrid = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 1.5rem;
  display: grid;
  gap: 1px;
  overflow: hidden;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  display: grid;
  gap: 0.35rem;
  padding: 1rem 0.75rem;
  text-align: center;
`;

const StatValue = styled.strong`
  font-size: 2rem;
  line-height: 1;
`;

const StatLabel = styled.span`
  font-size: 0.8rem;
  line-height: 1.35;
  text-transform: uppercase;
`;

const ShareRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.9rem 1.1rem;
  justify-content: center;
`;

const ShareIconLink = styled.a`
  align-items: center;
  border-radius: 999px;
  color: ${({ $accentColor }) => $accentColor};
  display: inline-flex;
  height: 2.5rem;
  justify-content: center;
  text-decoration: none;
  width: 2.5rem;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const ShareGlyph = styled.span`
  align-items: center;
  display: inline-flex;
  font-size: 1.35rem;
  font-weight: 700;
  justify-content: center;
  line-height: 1;
`;

const Footer = styled.div`
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.8rem;
  left: 0.75rem;
  position: absolute;
  bottom: 0.75rem;

  @media (min-width: 768px) {
    bottom: 1rem;
    left: 1rem;
  }
`;

const TermsLink = styled.a`
  color: inherit;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

function ArrowIcon() {
  return (
    <svg
      aria-hidden='true'
      fill='none'
      height='16'
      viewBox='0 0 16 16'
      width='16'
      xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M2 8H13M8.5 3.5L13 8L8.5 12.5'
        stroke='currentColor'
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth='1.75'
      />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg
      aria-hidden='true'
      fill='none'
      height='20'
      viewBox='0 0 20 20'
      width='20'
      xmlns='http://www.w3.org/2000/svg'>
      <rect
        height='12'
        rx='2'
        stroke='currentColor'
        strokeWidth='1.6'
        width='16'
        x='2'
        y='4'
      />
      <path
        d='M3 6L10 11L17 6'
        stroke='currentColor'
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth='1.6'
      />
    </svg>
  );
}

function FacebookIcon() {
  return (
    <ShareGlyph aria-hidden='true'>f</ShareGlyph>
  );
}

function XIcon() {
  return (
    <ShareGlyph aria-hidden='true'>x</ShareGlyph>
  );
}

const buildEmailShareHref = (shareLink, emailShareSubject, socialShareText) =>
  `mailto:?subject=${encodeURIComponent(emailShareSubject)}&body=${
    encodeURIComponent(`${socialShareText}\n\n${shareLink}`)
  }`;

const buildFacebookShareHref = (shareLink) =>
  `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareLink)}`;

const buildTwitterShareHref = (shareLink, socialShareText) =>
  `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareLink)}&text=${
    encodeURIComponent(socialShareText)
  }`;

export default function ReferralWidget({
  accentColor,
  attributes,
  backgroundImage,
  backgroundPosition,
  copiedButtonText,
  copyButtonText,
  dataTestId,
  description,
  emailShareSubject,
  errorDescription,
  errorHeading,
  headline,
  hideMetrics,
  hideSocialShare,
  loadingText,
  loginButtonText,
  loginDescription,
  loginHeading,
  loginMessage,
  panelBackgroundColor,
  previewState,
  shareLabel,
  shareWithLabel,
  socialShareText,
  termsText,
  termsUrl,
  title,
}) {
  const { user, userFetchState } = useUser();
  const [viewState, setViewState] = useState(VIEW_STATE.LOADING);
  const [profile, setProfile] = useState(null);
  const [copied, setCopied] = useState(false);

  const builderPreviewProfile = {
    ...previewReferrer,
    shareLink: buildReferralShareUrl(previewReferrer.referralCode),
  };

  const resolvedLoginMessage = loginMessage
    || [loginHeading, loginDescription].filter(Boolean).join('\n');

  useEffect(() => {
    if (!copied) return undefined;

    const timeoutId = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timeoutId);
  }, [copied]);

  useEffect(() => {
    if (Builder.isEditing && previewState !== 'live') {
      setViewState(previewState);
      return undefined;
    }

    if (userFetchState === fetchState.PENDING) {
      setViewState(VIEW_STATE.LOADING);
      return undefined;
    }

    if (userFetchState === fetchState.ERROR) {
      setViewState(VIEW_STATE.ERROR);
      return undefined;
    }

    if (Object.keys(user).length === 0) {
      setViewState(VIEW_STATE.LOGGED_OUT);
      return undefined;
    }

    let cancelled = false;

    const loadReferrer = async () => {
      setViewState(VIEW_STATE.LOADING);
      const response = await resolveMyReferrerProfile();
      if (cancelled) return;

      if ([200, 201].includes(response.status) && response.data?.referralCode) {
        setProfile({
          ...response.data,
          shareLink: buildReferralShareUrl(response.data.referralCode),
        });
        setViewState(VIEW_STATE.READY);
        return;
      }

      setViewState(VIEW_STATE.ERROR);
    };

    loadReferrer().catch((error) => {
      if (!cancelled) {
        // eslint-disable-next-line no-console
        console.error('Failed to load referral profile', error);
        setViewState(VIEW_STATE.ERROR);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [previewState, user, userFetchState]);

  const activeProfile = Builder.isEditing && previewState === 'ready'
    ? builderPreviewProfile
    : profile;

  const handleCopy = async () => {
    if (!activeProfile?.shareLink || !navigator.clipboard?.writeText) return;

    try {
      await navigator.clipboard.writeText(activeProfile.shareLink);
      setCopied(true);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to copy referral share link', error);
    }
  };

  const handleLogin = () => {
    redirectToLogin({
      asPath: window.location.pathname + window.location.search,
    });
  };

  const shareLinks = activeProfile?.shareLink
    ? [
      {
        ariaLabel: 'Share by email',
        href: buildEmailShareHref(
          activeProfile.shareLink,
          emailShareSubject,
          socialShareText
        ),
        icon: <MailIcon />,
      },
      {
        ariaLabel: 'Share on Facebook',
        href: buildFacebookShareHref(activeProfile.shareLink),
        icon: <FacebookIcon />,
      },
      {
        ariaLabel: 'Share on X',
        href: buildTwitterShareHref(activeProfile.shareLink, socialShareText),
        icon: <XIcon />,
      },
    ]
    : [];

  return (
    <Widget
      {...addIdAttribute(dataTestId)}
      {...attributes}
      $backgroundImage={backgroundImage}
      $backgroundPosition={backgroundPosition}>
      <Panel $panelBackgroundColor={panelBackgroundColor}>
        <PanelBody>
          {title ? <Label>{title}</Label> : null}

          <HeadlineBand $accentColor={accentColor}>
            <Headline $accentColor={accentColor}>{headline}</Headline>
          </HeadlineBand>

          <Description>{description}</Description>

          {viewState === VIEW_STATE.LOADING && (
            <StateStack>
              <StateMessage>{loadingText}</StateMessage>
            </StateStack>
          )}

          {viewState === VIEW_STATE.LOGGED_OUT && (
            <StateStack>
              <Callout>{resolvedLoginMessage}</Callout>
              <PrimaryButton
                $accentColor={accentColor}
                // eslint-disable-next-line react/jsx-no-bind
                onClick={handleLogin}
                type='button'>
                {loginButtonText}
                <ArrowIcon />
              </PrimaryButton>
            </StateStack>
          )}

          {viewState === VIEW_STATE.ERROR && (
            <StateStack>
              <Callout>{errorHeading}</Callout>
              <StateMessage>{errorDescription}</StateMessage>
            </StateStack>
          )}

          {viewState === VIEW_STATE.READY && activeProfile ? (
            <>
              <CopySection>
                <SectionLabel>{shareLabel}</SectionLabel>
                <CopyRow>
                  <LinkValue>{activeProfile.shareLink}</LinkValue>
                  <SecondaryButton
                    $accentColor={accentColor}
                    // eslint-disable-next-line react/jsx-no-bind
                    onClick={handleCopy}
                    type='button'>
                    {copied ? copiedButtonText : copyButtonText}
                  </SecondaryButton>
                </CopyRow>
              </CopySection>

              {!hideMetrics && (
                <StatsGrid>
                  <StatCard>
                    <StatValue>{activeProfile.stats?.clicksCount || 0}</StatValue>
                    <StatLabel>Clicks</StatLabel>
                  </StatCard>
                  <StatCard>
                    <StatValue>{activeProfile.stats?.referralsCount || 0}</StatValue>
                    <StatLabel>Successful Referrals</StatLabel>
                  </StatCard>
                  <StatCard>
                    <StatValue>{activeProfile.stats?.rewardsCount || 0}</StatValue>
                    <StatLabel>Rewards Earned</StatLabel>
                  </StatCard>
                </StatsGrid>
              )}

              {!hideSocialShare && shareLinks.length > 0 && (
                <ShareRow>
                  <SectionLabel as='span'>{shareWithLabel}</SectionLabel>
                  {shareLinks.map((shareLink) => (
                    <ShareIconLink
                      $accentColor={accentColor}
                      aria-label={shareLink.ariaLabel}
                      href={shareLink.href}
                      key={shareLink.ariaLabel}
                      rel='noopener noreferrer'
                      target='_blank'>
                      {shareLink.icon}
                    </ShareIconLink>
                  ))}
                </ShareRow>
              )}
            </>
          ) : null}
        </PanelBody>

        <Footer>
          {termsUrl ? (
            <TermsLink href={termsUrl}>{termsText}</TermsLink>
          ) : (
            <span>{termsText}</span>
          )}
        </Footer>
      </Panel>
    </Widget>
  );
}

ReferralWidget.propTypes = {
  accentColor: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  attributes: PropTypes.object,
  backgroundImage: PropTypes.string,
  backgroundPosition: PropTypes.string,
  copiedButtonText: PropTypes.string,
  copyButtonText: PropTypes.string,
  dataTestId: PropTypes.string,
  description: PropTypes.string,
  emailShareSubject: PropTypes.string,
  errorDescription: PropTypes.string,
  errorHeading: PropTypes.string,
  headline: PropTypes.string,
  hideMetrics: PropTypes.bool,
  hideSocialShare: PropTypes.bool,
  loadingText: PropTypes.string,
  loginButtonText: PropTypes.string,
  loginDescription: PropTypes.string,
  loginHeading: PropTypes.string,
  loginMessage: PropTypes.string,
  panelBackgroundColor: PropTypes.string,
  previewState: PropTypes.oneOf(['live', 'loading', 'logged-out', 'error', 'ready']),
  shareLabel: PropTypes.string,
  shareWithLabel: PropTypes.string,
  socialShareText: PropTypes.string,
  termsText: PropTypes.string,
  termsUrl: PropTypes.string,
  title: PropTypes.string,
};

ReferralWidget.defaultProps = {
  accentColor: '#40c7e6',
  attributes: {},
  backgroundImage: DEFAULT_BACKGROUND_IMAGE,
  backgroundPosition: 'center right',
  copiedButtonText: 'Copied',
  copyButtonText: 'Copy',
  dataTestId: null,
  description: DEFAULT_DESCRIPTION,
  emailShareSubject: 'Check out Glowforge',
  errorDescription: 'Refresh the page and try again.',
  errorHeading: 'We could not load your referral link',
  headline: 'Get Up to $500!',
  hideMetrics: false,
  hideSocialShare: false,
  loadingText: 'Loading your referral link...',
  loginButtonText: 'Log in',
  loginDescription: '',
  loginHeading: '',
  loginMessage: 'Log in to your account to get started',
  panelBackgroundColor: 'rgba(10, 56, 61, 0.96)',
  previewState: 'live',
  shareLabel: 'Share your referral link anywhere:',
  shareWithLabel: 'Or share with:',
  socialShareText: 'Take a look at Glowforge with my referral link.',
  termsText: 'Terms',
  termsUrl: 'https://glowforge.com/m/referral-terms',
  title: 'Refer a Friend',
};
